import React, { useContext, useEffect, useLayoutEffect } from 'react'
import { useSelector } from 'react-redux'
import { withStyles } from '@material-ui/core'

import moment from 'moment'
import _ from 'lodash'

import TextMessage from '../TextMessage'
import TextMessageButton from '../TextMessageButton'
import ImageMessage from '../ImageMessage'
import AudioMessage from '../AudioMessage'
import VideoMessage from '../VideoMessage'
import TemplateMessage from '../CarouselMessage'
import MessageWrap from '../MessageWrap'
import FileMessage from '../FileMessage'

import ActionMessage from '../ActionMessage'
import { styles } from './styles'
import { ChatHistoryElementType } from 'models/ChatMessagesTypes'
import { SharedWebsocketContext } from 'contexts/webSockets'
import { READ } from 'constants/statuses'
import ConversationIdLine from '../ConversationIdLine'
import { getPostbackIds } from 'helpers/getPostbackIds'
import { getUserRole, ROLE } from 'security'

interface Props {
  messages: ChatHistoryElementType[]
  msg: ChatHistoryElementType

  reverse: boolean
  highlight: boolean
  showConversationId: boolean
  showReactionElementText: boolean
  searchQuery: string
}

const Message: React.FC<Props> = props => {
  const { msg, reverse, highlight, showConversationId,
    showReactionElementText, messages, searchQuery } = props
  const { sendStatus } = useContext(SharedWebsocketContext)

  const botId = Number(window.location.pathname.split('/').find((_, i, arr) => arr[i - 1] === 'bot') || 0)
  const isAdmin = [ROLE.ADMIN, ROLE.CARECO_ADMIN].includes(getUserRole(botId))
  const isAiFeedbackEnabled = useSelector((state: any) => state.activeBot?.isAiFeedbackEnabled)

  /* eslint-enable no-console */
  const nativeMessage = msg?.nativeMessage ? JSON.parse(msg.nativeMessage) : null
  let message = msg?.messagePreviewTranslated ? JSON.parse(msg.messagePreviewTranslated) : (msg.messagePreview ? JSON.parse(msg.messagePreview) : null)

  if (!message && nativeMessage) {
    message = nativeMessage
  }

  const translatedText = JSON.parse(msg.messagePreviewTranslated)?.text?.toLowerCase().replaceAll('\\u0027', '\'').replaceAll(' ', '').replaceAll('s', '')
  const previewText = JSON.parse(msg.messagePreview)?.text?.toLowerCase().replaceAll('\\u0027', '\'').replaceAll(' ', '').replaceAll('s', '')
  const isTranslated = translatedText !== previewText
  const lastEventAt = moment.utc(msg.lastEventAt).local().format('hh:mm A')
  /* eslint-enable no-console */

  const isTextMessage = ((message?.text && !message?.buttons?.length) || message?.postback) && !message.imageUrl
  const isTextMessageWithButtons = message?.text && message?.buttons?.length
  const isTemplateMessage = message?.elements?.length
  const mediaType = _.get(message, 'attachments[0].type') || _.get(message, 'media[0].type')
  const isImageMessage = message?.imageUrl || mediaType?.toLowerCase() === 'image'
  const isAudioMessage = mediaType?.toLowerCase() === 'audio'
  const isVideoMessage = mediaType?.toLowerCase() === 'video'
  const mediaUrl = _.get(message, 'attachments[0].url') || _.get(message, 'attachments[0].payload.url')
  const isFileMessage = nativeMessage?.message?.type === 'file'

  const isRtl = message?.isRtl || msg?.isRtl
  const isReceived = reverse ? !message?.isFromUser : message?.isFromUser
  const enableReaction = _.get(nativeMessage, 'message.enableReaction', false)
  const reaction = _.get(message, 'reaction', { title: '', value: '' })

  useEffect(() => {
    onVisibilityChange()
  }, [])

  useLayoutEffect(() => {
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [])

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible' && !msg?.isFromBot && msg?.messageStatus !== READ) {
      sendStatus(READ, msg)
    }
  }

  const renderMessage = () => {
    if (isFileMessage) {
      return <FileMessage message={nativeMessage} />
    } else if (isTextMessage) {
      return <TextMessage message={message} isRtl={isRtl} isReceived={isReceived} highlight={highlight}
        searchQuery={searchQuery} />
    } else if (isTextMessageWithButtons) {
      return <TextMessageButton
        message={message}
        isRtl={isRtl}
        highlight={highlight}
        postbackIds={getPostbackIds(messages)}
        searchQuery={searchQuery}
      />
    } else if (isImageMessage) {
      return <ImageMessage imgUrl={message.imageUrl || mediaUrl} highlight={highlight} />
    } else if (isAudioMessage) {
      return <AudioMessage url={mediaUrl} />
    } else if (isVideoMessage) {
      return <VideoMessage url={mediaUrl} />
    } else if (isTemplateMessage) {
      return <TemplateMessage
        isRtl={isRtl}
        payload={message}
        highlight={highlight}
        postbackIds={getPostbackIds(messages)}
        searchQuery={searchQuery}
      />
    }
  }

  return (
    <>
      {nativeMessage?.action && <ActionMessage action={nativeMessage?.action} />}

      {showConversationId && <ConversationIdLine conversationId={msg?.conversationId} />}

      {message && (
        <MessageWrap
          lastEventAt={lastEventAt}
          isReceived={isReceived}
          status={msg?.messageStatus}
          enableReaction={enableReaction}
          reaction={reaction}
          showReactionElementText={showReactionElementText}
          message={message}
          postbackIds={getPostbackIds(messages)}
          isTranslated={isTranslated}
          feedback={msg.feedback}
          isAdmin={isAdmin}
          isAiFeedbackEnabled={isAiFeedbackEnabled}
        >
          {renderMessage()}
        </MessageWrap>
      )}
    </>
  )
}

export default withStyles(styles)(Message)
